import { useEffect, useState } from 'react'
import { IconCompress, IconMenu, IconMoon, IconSun } from '../icons'
import { useAuth } from '../../context/AuthContext'
import { StoreSwitcher } from './StoreSwitcher'
import { useUI } from '../../context/UIContext'
import { useTheme } from '../theme-provider'

export function Navbar({
  title,
  subtitle,
  posFocusActive = false,
}: {
  title: string
  subtitle?: string
  /** When true, sidebar is hidden for POS focus mode. */
  posFocusActive?: boolean
}) {
  const { user, logout, loading } = useAuth()
  const { toggleSidebar, setPosFocusMode } = useUI()
  const { setTheme, resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const displayName = user?.name || 'Admin'
  const initial = displayName.charAt(0).toUpperCase()
  const isDark = resolvedTheme === 'dark'

  return (
    <header className="glass z-20 shrink-0 border-b border-border">
      <div className="flex h-14 items-center justify-between gap-2 px-4 sm:h-16 sm:gap-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
          {posFocusActive ? (
            <button
              type="button"
              onClick={() => setPosFocusMode(false)}
              className="flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-xl border border-border bg-card/80 px-3 text-sm font-medium text-foreground shadow-sm transition hover:bg-muted"
              aria-label="Exit focus mode and show menu"
            >
              <IconCompress className="h-4 w-4 shrink-0" aria-hidden />
              <span className="hidden sm:inline">Show menu</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={toggleSidebar}
              className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-border bg-card/80 text-foreground shadow-sm transition hover:bg-muted lg:hidden"
              aria-label="Open menu"
            >
              <IconMenu className="h-5 w-5" aria-hidden />
            </button>
          )}

          <div className="min-w-0 py-1">
            <h1 className="truncate text-base font-semibold tracking-tight text-foreground sm:text-lg lg:text-xl">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-0.5 hidden truncate text-sm text-muted-foreground sm:block">{subtitle}</p>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
          <div className="hidden lg:contents">
            <StoreSwitcher />
          </div>

          <button
            type="button"
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl border border-border bg-card/80 text-foreground shadow-sm transition hover:bg-muted disabled:opacity-50"
            disabled={!mounted}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {mounted ? (
              isDark ? (
                <IconSun className="h-5 w-5" aria-hidden />
              ) : (
                <IconMoon className="h-5 w-5" aria-hidden />
              )
            ) : (
              <IconMoon className="h-5 w-5 opacity-50" aria-hidden />
            )}
          </button>

          <div
            className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground ring-2 ring-card sm:hidden"
            aria-hidden
          >
            {initial}
          </div>

          <div className="hidden items-center gap-3 sm:flex">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground ring-2 ring-card">
              {initial}
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-foreground">{displayName}</p>
              {user?.email ? (
                <p className="max-w-[200px] truncate text-xs text-muted-foreground">{user.email}</p>
              ) : null}
            </div>
          </div>

          <button
            type="button"
            onClick={() => logout()}
            disabled={loading}
            className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-border bg-card px-2.5 py-2 text-xs font-medium text-foreground shadow-sm transition hover:bg-muted disabled:pointer-events-none disabled:opacity-50 sm:px-4 sm:py-2.5 sm:text-sm"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="overflow-hidden border-t border-border/70 px-4 py-2 sm:px-6 lg:hidden">
        <StoreSwitcher className="w-full" layout="stacked" />
      </div>
    </header>
  )
}
